import { TreeView, TreeViewRef } from "devextreme-react/tree-view";
import * as events from "devextreme/events";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/auth";
import { useNavigation } from "../../contexts/navigation";
import { ThemeContext } from "../../theme/theme";
import { RoleEnum, type SideNavigationMenuProps } from "../../types";
import "./SideNavigationMenu.scss";

export type SideNavigationItem = {
  expanded: boolean;
  path: string;
  text: string;
  icon: string;
  items: Array<{
    text: string;
    path: string;
  }>;
};

export const SideNavigationMenu = ({
  children,
  selectedItemChanged,
  openMenu,
  compactMode,
  onMenuReady,
}: React.PropsWithChildren<SideNavigationMenuProps>) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const themeContext = useContext(ThemeContext);

  const isEmployee = user?.roles?.includes(RoleEnum.Pracownik) ?? false;
  const isForeman = user?.roles?.includes(RoleEnum.Majster) ?? false;
  const isManager = user?.roles?.includes(RoleEnum.Kierownik) ?? false;
  const isSupervisor = user?.roles?.includes(RoleEnum.Manager) ?? false;
  const isCompanyStaff = user?.roles?.includes(RoleEnum.KadrySpolki) ?? false;
  const isCompanyAdministrator =
    user?.roles?.includes(RoleEnum.AdministratorSpolki) ?? false;
  const isHeadAdministrator =
    user?.roles?.includes(RoleEnum.AdministratorGlowny) ?? false;
  const isTemporaryManager = user?.isTimeManager;

  function normalizePath() {
    const menuItems = [
      {
        class: "bookmark-home",
        expanded: true,
        path: "/",
        text: t("sideMenu.homePage.header"),
        icon: "description",
        items: [],
        visible: true,
      },
      {
        expanded: true,
        path: "",
        text: t("sideMenu.employees.header"),
        icon: "user",
        items: [
          {
            text: t("sideMenu.employees.list"),
            path: "/employees",
            visible: (() => {
              // tylko dla administratorów spółki i głównego administratora
              if (isCompanyAdministrator || isHeadAdministrator) {
                return true;
              }

              return false;
            })(),
          },
        ],
        visible: true,
      },
      {
        expanded: true,
        path: "",
        text: t("sideMenu.workPlan.header"),
        icon: "eventall",
        class: "bookmark-work-plan",
        items: [
          {
            text: t("sideMenu.workPlan.availability"),
            path: "/availability",
            visible: (() => {
              // pracownik z umową cywilnoprawną (contractType = 1)
              if (isEmployee && user?.contractType === 1) {
                return true;
              }

              if (
                isForeman ||
                isManager ||
                isSupervisor ||
                isCompanyStaff ||
                isCompanyAdministrator ||
                isHeadAdministrator ||
                isTemporaryManager
              ) {
                return true;
              }

              return false;
            })(),
          },
          {
            text: t("sideMenu.workPlan.workPlan"),
            path: "/work-plan",
            visible: true,
          },
          {
            text: t("sideMenu.workPlan.schedule"),
            path: "/schedule",
            visible: (() => {
              if (isManager) {
                return true;
              }

              return false;
            })(),
          },
        ],
        visible: true,
      },
      {
        expanded: true,
        path: "",
        text: t("sideMenu.workTime.header"),
        icon: "clock",
        class: "bookmark-work-time",
        items: [
          {
            text: t("sideMenu.workTime.presences"),
            path: "/presence",
            visible: true,
          },
          {
            text: t("sideMenu.workTime.holidays"),
            path: "/holiday",
            class: "holiday-bookmark",
            visible: true,
          },
          {
            text: t("sideMenu.workTime.hourlySummary"),
            path: "/hourly-summary",
            visible: (() => {
              if (
                isManager ||
                isSupervisor ||
                isCompanyStaff ||
                isCompanyAdministrator ||
                isHeadAdministrator ||
                isTemporaryManager
              ) {
                return true;
              }

              return false;
            })(),
          },
        ],
        visible: true,
      },
      {
        expanded: true,
        path: "/projects",
        text: t("sideMenu.projects.header"),
        icon: "folder",
        items: [],
        visible: (() => {
          if (isSupervisor || isCompanyAdministrator || isHeadAdministrator) {
            return true;
          }

          return false;
        })(),
      },
      {
        expanded: true,
        path: "/transfer-of-employees",
        text: t("sideMenu.employeesTransfer2.header"),
        icon: "repeat",
        items: [],
        visible: (() => {
          if (
            isManager ||
            isSupervisor ||
            isCompanyStaff ||
            isCompanyAdministrator ||
            isHeadAdministrator ||
            isTemporaryManager
          ) {
            return true;
          }

          return false;
        })(),
      },
      {
        expanded: true,
        path: "",
        text: t("sideMenu.awards.header"),
        icon: "gift",
        visible: (() => {
          if (
            isManager ||
            isSupervisor ||
            isCompanyStaff ||
            isCompanyAdministrator ||
            isHeadAdministrator ||
            isTemporaryManager
          ) {
            return true;
          }
          return false;
        })(),
        items: [
          {
            path: "/awards",
            text: t("sideMenu.awards.list"),

            visible: (() => {
              if (
                isManager ||
                isSupervisor ||
                isCompanyStaff ||
                isCompanyAdministrator ||
                isHeadAdministrator ||
                isTemporaryManager
              ) {
                return true;
              }
              return false;
            })(),
          },
          {
            path: "/awards/summary",
            text: t("sideMenu.awards.summary"),

            visible: (() => {
              if (
                isManager ||
                isSupervisor ||
                isCompanyStaff ||
                isCompanyAdministrator ||
                isHeadAdministrator ||
                isTemporaryManager
              ) {
                return true;
              }
              return false;
            })(),
          },
        ],
      },

      {
        expanded: true,
        path: "",
        text: t("sideMenu.employeesEvaluation.header"),
        icon: "favorites",
        items: [
          {
            text: t("sideMenu.employeesEvaluation.sheets"),
            path: "/sheets",
            visible: (() => {
              if (
                isManager ||
                isSupervisor ||
                isCompanyStaff ||
                isCompanyAdministrator ||
                isHeadAdministrator ||
                isTemporaryManager
              ) {
                return true;
              }

              return false;
            })(),
          },
          {
            text: t("sideMenu.employeesEvaluation.rate"),
            path: "/rate",
            visible: true,
          },
          {
            text: t("sideMenu.employeesEvaluation.summary"),
            path: "/evalutation-summary",
            visible: (() => {
              if (
                isManager ||
                isSupervisor ||
                isCompanyStaff ||
                isCompanyAdministrator ||
                isHeadAdministrator ||
                isTemporaryManager
              ) {
                return true;
              }

              return false;
            })(),
          },
        ],
        visible: true,
      },
      {
        expanded: true,
        path: "/submitting-comments",
        text: t("sideMenu.commentsSubmitting.header"),
        icon: "email",
        items: [],
        visible: true,
      },
      {
        expanded: true,
        path: "/reading-notes",
        text: t("sideMenu.readingNotes.header"),
        icon: "taskcomplete",
        items: [],
        visible: (() => {
          if (isHeadAdministrator) {
            return true;
          }

          return false;
        })(),
      },
      {
        expanded: true,
        path: "/announcements",
        text: t("sideMenu.announcements.header"),
        icon: "selectall",
        items: [],
        visible: true,
      },
      {
        expanded: true,
        path: "/documents",
        text: t("sideMenu.documents.header"),
        icon: "doc",
        items: [],
        visible: (() => {
          if (
            isEmployee ||
            isForeman ||
            isManager ||
            isSupervisor ||
            isCompanyStaff
          ) {
            return true;
          }

          return false;
        })(),
      },
    ];

    return menuItems
      .filter(({ visible }) => visible === true)
      .filter((item) => {
        // jeżeli wszystkie podelementy są niewidoczne to ukrywa też element główny (rozwijany)
        if (
          item.path === "" &&
          !item.items.some(({ visible }) => visible === true)
        ) {
          return false;
        }

        return true;
      })
      .map((item) => {
        return {
          ...item,
          items: item.items.filter(({ visible }) => visible === true),
        };
      });
  }

  const items: SideNavigationItem[] = useMemo(normalizePath, [t]);

  const {
    navigationData: { currentPath },
  } = useNavigation();

  const treeViewRef = useRef<TreeViewRef>(null);
  const wrapperRef = useRef();
  const getWrapperRef = useCallback(
    (element) => {
      const prevElement = wrapperRef.current;
      if (prevElement) {
        events.off(prevElement, "dxclick");
      }

      wrapperRef.current = element;
      events.on(element, "dxclick", (e: React.PointerEvent) => {
        openMenu(e);
      });
    },
    [openMenu],
  );

  useEffect(() => {
    const treeView = treeViewRef.current && treeViewRef.current.instance();
    if (!treeView) {
      return;
    }

    if (currentPath !== undefined) {
      treeView.selectItem(currentPath);
      treeView.expandItem(currentPath);
    }

    if (compactMode) {
      treeView.collapseAll();
    }
  }, [currentPath, compactMode]);

  const itemRender = (item) => {
    const className = item.class || "";
    return (
      <div className={`treeview-item ${className}`}>
        {item.icon && <span className={`dx-icon dx-icon-${item.icon}`} />}
        {item.text}
      </div>
    );
  };

  return (
    <div
      className={`dx-swatch-additional side-navigation-menu theme-${themeContext?.theme}`}
      ref={getWrapperRef}
    >
      {children}
      <div
        className={`menu-container theme-dependent theme-${themeContext?.theme}`}
      >
        <TreeView
          ref={treeViewRef}
          items={items}
          keyExpr="path"
          selectionMode="single"
          focusStateEnabled={false}
          expandEvent="click"
          onItemClick={selectedItemChanged}
          onContentReady={onMenuReady}
          itemRender={itemRender}
          width="100%"
        />
      </div>
    </div>
  );
};
